import { useEffect, useState } from "react";
import { getScoring, playAgain } from "@/lib/api";
import { ScoreSheet, TiebreakLine } from "@/components/game/ScoreSheet";
import { toast } from "sonner";
import { Download, FileText, LayoutGrid, X } from "lucide-react";

/**
 * EndgameOverlay — full-screen final-scoring breakdown.
 *
 * Pulls the scoring breakdown from `/api/games/{id}/scoring` and renders
 * the rows per player (Population · Resilience with its parts · Debt · Final
 * score) plus the tiebreak line, which ending happened (3° or 12 rounds), the rule
 * that ranked the players and the final meter. Winners are highlighted; ties show
 * "Shared victory".
 *
 * Play Again calls `/play_again` and triggers the parent to refresh.
 */
// canPlayAgain: online only the leader sees Play again. onError: online error handling (spec status table).
// onClose: X / "View board" — the final board stays on screen (read-only); the top bar's "Show scores"
// brings this back. onDownloadImage / onDownloadLog: client-side downloads, for every seat.
export default function EndgameOverlay({ game, onPlayAgain, canPlayAgain = true, onError, onClose, onDownloadImage, onDownloadLog, downloading = false }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!game?.id) return;
    getScoring(game.id)
      .then((d) => setData(d))
      .catch(() => setData(null));
  }, [game?.id]);

  const played = game.rounds_played ?? Math.max(0, (game.round || 1) - 1);
  const early = game.endgame_reason === "three_degrees";
  const reasonLabel = early
    ? `The world reached 3° in round ${played}`
    : game.endgame_reason === "max_rounds"
      ? `${played} rounds played`
      : "The coast holds — for now.";
  const ruleLabel = early
    ? "Ranked by resilience only. Debt is not subtracted."
    : "Ranked by population + resilience − debt.";
  const c = game.carbon;

  const ranking = data?.ranking || [];
  const sharedVictory = data?.shared_victory ?? false;
  const winners = data?.winner_colors || [];

  const onPlayAgainClick = async () => {
    if (!game?.id) return;
    try {
      setBusy(true);
      const next = await playAgain(game.id);
      toast.success("Same players, fresh board — Phase 1 begins.");
      onPlayAgain(next);
    } catch (e) {
      if (onError) onError(e, "Could not reset the game");
      else toast.error(e?.response?.data?.detail || "Could not reset the game");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="endgame-overlay"
      className="fixed inset-0 z-50 flex items-start justify-center bg-black/85 backdrop-blur-sm overflow-y-auto py-8 px-4"
    >
      <div className="relative w-full max-w-3xl bg-[color:var(--card)] rounded-[10px] shadow-[var(--shadow-card)] p-7">
        {onClose && (
          <button
            type="button"
            data-testid="endgame-close-btn"
            onClick={onClose}
            aria-label="Close the scores and view the final board"
            title="Close (view the final board)"
            className="btn-ghost absolute"
            style={{ top: 14, right: 14, width: 36, height: 36, padding: 0, justifyContent: "center" }}
          >
            <X size={20} strokeWidth={1.8} />
          </button>
        )}
        <div className="label-kicker text-[color:var(--ink-3)]" data-testid="endgame-reason">
          ENDGAME — {reasonLabel}
        </div>
        <h2 className="font-display text-4xl font-bold tracking-tight text-[color:var(--ink)] mt-2">
          {sharedVictory ? "Shared victory" : "Final scoring"}
        </h2>
        <div className="mt-1 text-sm text-[color:var(--ink-2)]" data-testid="endgame-rule">{ruleLabel}</div>
        {c && (
          <div className="mt-3 flex items-center gap-3" data-testid="endgame-meter" title={`Carbon meter ${c.meter} · ${c.temp}°`}>
            <span className="label-kicker text-[color:var(--ink-3)] shrink-0">GLOBAL TEMP</span>
            <div className="relative flex-1 h-2 rounded-full" style={{ background: "var(--line)", maxWidth: 260 }}>
              <div className="absolute left-0 top-0 h-2 rounded-full" style={{ width: `${Math.min(100, ((c.temp ?? 0) / (c.max_degrees || 3)) * 100)}%`, background: early ? "var(--danger)" : "var(--sea)" }} />
              {Array.from({ length: (c.max_degrees || 3) - 1 }).map((_, i) => (
                <span key={i} className="absolute top-0 h-2" style={{ left: `${((i + 1) / (c.max_degrees || 3)) * 100}%`, width: 1, background: "var(--ink-4)" }} />
              ))}
            </div>
            <span className="text-sm font-bold tabular-nums text-[color:var(--ink)]">+{Number(c.temp ?? 0).toFixed(1)}°</span>
            <span className="text-xs text-[color:var(--ink-3)]">meter {c.meter} · sea rose {game.slr_events} time{game.slr_events === 1 ? "" : "s"}</span>
          </div>
        )}
        {sharedVictory && (
          <div
            className="mt-2 text-sm text-[color:var(--ink)] font-bold"
            data-testid="shared-winners"
          >
            {winners.map((c) => c.toUpperCase()).join(" & ")} share the win.
          </div>
        )}

        <ul
          className="mt-6 grid gap-3 grid-cols-2"
          data-testid="endgame-breakdown-list"
        >
          {ranking.map((row) => (
            <ScoreSheet
              key={row.color}
              row={row}
              highlight={winners.includes(row.color)}
              weights={data?.weights}
              final
              player={game.players.find((p) => p.color === row.color)}
            />
          ))}
        </ul>
        <TiebreakLine ranking={ranking} players={game.players} order={data?.tiebreaker_order} mode={data?.mode} />

        <div className="mt-6 flex flex-wrap items-center justify-end gap-3">
          {onDownloadImage && (
            <button data-testid="endgame-download-image-btn" onClick={onDownloadImage} disabled={downloading} className="btn-tactile">
              <Download size={16} strokeWidth={1.8} /> Download board image
            </button>
          )}
          {onDownloadLog && (
            <button data-testid="endgame-download-log-btn" onClick={onDownloadLog} className="btn-tactile">
              <FileText size={16} strokeWidth={1.8} /> Download game log
            </button>
          )}
          {onClose && (
            <button data-testid="endgame-view-board-btn" onClick={onClose} className="btn-tactile">
              <LayoutGrid size={16} strokeWidth={1.8} /> View board
            </button>
          )}
          {canPlayAgain && (
          <button
            data-testid="play-again-btn"
            onClick={onPlayAgainClick}
            disabled={busy}
            className="btn-tactile btn-tactile--primary"
          >
            Play again →
          </button>
          )}
        </div>
      </div>
    </div>
  );
}
